import { useState, useEffect, useCallback } from 'react';
import { useParallaxTransform } from '@/hooks/useParallax';
import SectionTitle from '@/components/base/SectionTitle';

const services = [
  {
    icon: 'ri-music-2-line',
    title: 'Festivals & grands événements',
    desc: 'Un bar mobile à haut débit, pensé pour servir vite, bien et sans rupture.',
    image:
      "https://storage.readdy-site.link/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/7f9ca9af-ee7b-45f0-a1b4-27cf500fcae2_image_Festival.png?v=5f4525c1d0aee353a518c0dc8cdb21f6",
  },
  {
    icon: 'ri-store-2-line',
    title: 'Marchés & foires',
    desc: 'Une solution clé en main pour renforcer votre offre boisson, simplement',
    image:
      "https://storage.readdy-site.link/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/4594c8ab-475b-4871-a6ff-ae24b4c3dac1_march.png?v=98096778bdf1bc4683eb82c53b6af3ea",
  },
  {
    icon: 'ri-community-line',
    title: 'Manifestations publiques',
    desc: 'Un bar fiable, rapide à installer et adapté aux fêtes populaires',
    image:
      "https://storage.readdy-site.link/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/5a3f8c6b-1703-4f4a-9c05-4dd309cfd427_Manifestations-Communales.png?v=4deeb0ec7dfff7a940c241fdf119057e",
  },
  {
    icon: 'ri-building-line',
    title: "Événements d'entreprise",
    desc: 'Une infrastructure élégante et performante pour vos soirées, lancements et événements corporate.',
    image:
      "https://storage.readdy-site.link/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/6ea40c94-91b6-4927-ad90-6101a1d7a2f8_vnements-dEntreprise.png?v=90df8a7213928119aa26191de471444c",
  },
  {
    icon: 'ri-heart-line',
    title: 'Mariages & événements privés',
    desc: 'Un bar mobile discret, esthétique et efficace pour vos plus beaux moments.',
    image:
      "https://storage.readdy-site.link/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/f763215b-97d9-42e9-a0c6-50cde342c61a_Mariages.png?v=3aa7a26454f04cc48df765ad3e2d900a",
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Activations de Marque',
    desc: 'Une structure personnalisable pour crée un point de service visible, fluide et impactant.',
    image:
      "https://storage.readdy-site.link/project_files/d8db5950-dd7c-40d9-b2de-72ddcdf21388/1d919f10-3824-4d26-91f5-769ff539a76a_marque.png?v=ac7678f13b49d8de93885af912949fc6",
  },
];

export default function Services() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const titleRef = useParallaxTransform(0.06);

  useEffect(() => {
    const update = () => {
      setItemsPerView(window.innerWidth >= 768 ? 3 : 1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const totalSlides = Math.ceil(services.length / itemsPerView);
  const maxSlideIndex = Math.max(totalSlides - 1, 0);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxSlideIndex));
  }, [maxSlideIndex]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  useEffect(() => {
    setCurrentIndex((prev) => Math.min(prev, maxSlideIndex));
  }, [maxSlideIndex]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      goNext();
    }
    if (touchStart - touchEnd < -50) {
      goPrev();
    }
  };

  return (
    <section id="services" className="py-20 sm:py-28 lg:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          label="Nos Clients"
          title="Nos Clients"
          delay={100}
        />

        <div className="relative animate-fade-in-up delay-400">
          {/* Flèche gauche */}
          <button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-4 lg:-translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-[#00A8B5] hover:bg-[#00A8B5] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            aria-label="Précédent"
          >
            <i className="ri-arrow-left-line text-lg md:text-xl"></i>
          </button>

          {/* Carrousel */}
          <div
            className="overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                transform: `translateX(-${currentIndex * 100}%)`,
              }}
            >
              {services.map((s) => (
                <div
                  key={s.title}
                  className="w-full md:w-1/3 flex-shrink-0 px-3"
                >
                  <div
                    className="h-[400px] sm:h-[420px] lg:h-[460px] rounded-lg border relative overflow-hidden group bg-cover bg-center transition-all duration-[250ms] ease-out hover:scale-[1.02] hover:-translate-y-1 hover:shadow-lg"
                    style={{
                      borderColor: '#00A8B533',
                      backgroundImage: `url('${s.image}')`,
                    }}
                  >
                    <div className="absolute inset-0 bg-black/45 group-hover:bg-black/25 transition-all duration-500"></div>
                    <div className="relative z-10 h-full flex flex-col justify-end p-8 sm:p-10">
                      <div className="w-12 h-12 flex items-center justify-center mb-6 rounded-full bg-white/10 backdrop-blur-sm group-hover:bg-[#00A8B5]/20 group-hover:scale-110 transition-all duration-500">
                        <i className={`${s.icon} text-2xl text-[#00A8B5]`}></i>
                      </div>
                      <h3
                        className="text-xl font-semibold mb-4 text-[#00A8B5] group-hover:translate-x-1 transition-transform duration-500 h-[4rem] flex items-end"
                        style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}
                      >
                        {s.title}
                      </h3>
                      <p
                        className="text-white group-hover:translate-x-1 transition-transform duration-500 delay-75 h-[4.5rem] line-clamp-3"
                        style={{ textShadow: '0 1px 3px rgba(0,0,0,0.7)' }}
                      >
                        {s.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Flèche droite */}
          <button
            onClick={goNext}
            disabled={currentIndex >= maxSlideIndex}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-4 lg:translate-x-6 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-[#00A8B5] hover:bg-[#00A8B5] hover:text-white transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
            aria-label="Suivant"
          >
            <i className="ri-arrow-right-line text-lg md:text-xl"></i>
          </button>
        </div>

        {/* Indicateurs */}
        <div className="flex justify-center items-center gap-2 mt-8">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`h-2.5 rounded-full transition-all cursor-pointer ${
                i === currentIndex
                  ? 'bg-[#00A8B5] w-6'
                  : 'bg-[#00A8B5]/30 w-2.5 hover:bg-[#00A8B5]/50'
              }`}
              aria-label={`Aller à la slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}